import React, { ComponentProps } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useRouting } from 'hooks/useRouting';
import {
  UseQueryStateParameters,
  urlEncode,
  useQueryState,
} from 'hooks/useQueryState';
import { getInvestmentTrustFundByAsset, getUserBroker } from 'config/api';
import {
  InvestmentTrustFund,
  InvestmentTrustType,
  PortfolioAssetType,
  TargetId,
  Account,
} from '../utils/type';
import {
  INVESTMENT_TRUST_TYPE,
  portfolioTypeToString,
} from '../utils/portfolioInfo';
import { useBirthday } from '../utils/birthday';
import { InvestmentTrustTabs } from '../components/features/portfolio-analysis/share/InvestmentTrustTabs';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { investmentTrustTypeKey } from './PortfolioAnalysisAssetInformation';
import { InvestmentTrustItemListBase } from '../components/features/portfolio-analysis/investment-product/InvestmentTrustItemListBase';

export type Company =
  | {
      tag: 'selected';
      value: string;
    }
  | {
      tag: 'notSelected';
    };

export type ItemsLength = {
  JP_MF: number;
  JP_ETF: number;
  US_ETF: number;
};

export const itemsCount = (
  items: InvestmentTrustFund['itemsByInvestmentType'],
  company: Company
): ItemsLength => {
  return {
    JP_MF: items
      .filter(item => item.investmentType === 'JP_MF')
      .filter(
        item =>
          company.tag === 'notSelected' ||
          item.salesCompany.includes(company.value)
      ).length,
    JP_ETF: items.filter(item => item.investmentType === 'JP_ETF').length,
    US_ETF: items.filter(item => item.investmentType === 'US_ETF').length,
  };
};

const PortfolioAnalysisInvestmentProductInternal = ({
  accounts,
  investmentTrustFund,
  type,
}: {
  accounts: Account[];
  investmentTrustFund: InvestmentTrustFund;
  type: PortfolioAssetType;
}) => {
  const pushRouting = (({ pushRouting }) => (path: string) => () =>
    pushRouting({ path }, true, window.scrollY))(useRouting());
  const getDetailPath = (code: string) => `/tradedetail/${code}`;

  const selectedInvestmentTrustTypeEncode: UseQueryStateParameters<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType']
  >['encode'] = state => {
    return urlEncode(state);
  };

  const selectedInvestmentTrustTypeDecode: UseQueryStateParameters<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType']
  >['decode'] = value => {
    switch (value) {
      case INVESTMENT_TRUST_TYPE['国内ETF'].type:
      case INVESTMENT_TRUST_TYPE['国内投信'].type:
      case INVESTMENT_TRUST_TYPE['米国ETF'].type:
        return { tag: 'Success', value };
      default:
        return { tag: 'Failed' };
    }
  };

  const [selectedInvestmentTrustType, setInvestmentTrustType] = useQueryState<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType'],
    typeof investmentTrustTypeKey
  >(
    selectedInvestmentTrustTypeEncode,
    selectedInvestmentTrustTypeDecode,
    INVESTMENT_TRUST_TYPE['国内投信'].type,
    investmentTrustTypeKey
  );

  const accounts_: ComponentProps<
    typeof InvestmentTrustItemListBase
  >['accounts'] =
    accounts.length === 0
      ? ({ tag: 'notAggregated' } as const)
      : ({
          tag: 'aggregated',
          value: accounts.map((account: Account) => ({
            aggregateSource: account.aggregateSource,
            brokerName: account.brokerName,
          })),
        } as const);

  return (
    <PremiumCheck content={<TargetPremium />}>
      <InvestmentTrustItemListBase
        isDetail
        type={type}
        items={investmentTrustFund.itemsByInvestmentType}
        onClickInvestmentTrustItem={code => pushRouting(getDetailPath(code))}
        accounts={accounts_}
        selectedInvestmentTrustType={selectedInvestmentTrustType}
        setInvestmentTrustType={setInvestmentTrustType}
        TabFilteredItemsDescription={() => (
          <>ベンチマーク（投資対象）参考指標：{investmentTrustFund.benchMark}</>
        )}
        headerTitle={`「${portfolioTypeToString(
          type
        )}」に投資できる投資信託・ETF`}
      />
    </PremiumCheck>
  );
};

// MEMO: 「すべて見る」押下後の「〇〇」に投資できる投資信託・ETFコンポーネント
const PortfolioAnalysisInvestmentProduct = (): React.ReactElement => {
  const { id, type } = useParams<{ id: string; type: PortfolioAssetType }>();

  const { data: accounts, isFetching: userBrokerIsFetching } = useQuery(
    'portfolioAnalysisInvestmentProductUserBroker',
    () => getUserBrokerAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: investmentTrustFund,
    isFetching: investmentTrustFundByAssetIsFetching,
  } = useQuery(
    'portfolioAnalysisInvestmentProductInvestmentTrustFundByAsset',
    () => getInvestmentTrustFundByAssetAPI(id, type),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (
    accounts === null ||
    accounts === undefined ||
    investmentTrustFund === null ||
    investmentTrustFund === undefined ||
    userBrokerIsFetching ||
    investmentTrustFundByAssetIsFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PortfolioAnalysisInvestmentProductInternal
      {...{ accounts, investmentTrustFund, type }}
    />
  );
};

const getUserBrokerAPI = async () => {
  const ret_ = await getUserBroker();
  if (ret_ === null || ret_.Items === null) return null;
  const ret = ret_.Items.map(item => {
    return {
      totalAssetAmount: item.TotalAssetAmount,
      account: item.Account,
      available: item.Available,
      brokerName: item.BrokerName,
      aggregateSource: item.AggregateSource,
    };
  }) as Account[];

  return ret;
};

const getInvestmentTrustFundByAssetAPI = async (
  id: TargetId,
  type: PortfolioAssetType
) => {
  const res_ = await getInvestmentTrustFundByAsset(id, type);
  const res = {
    itemsByInvestmentType: res_.ItemsByInvestmentType.map(item => {
      return {
        investmentType: item.InvestmentType as InvestmentTrustType,
        held: item.Held,
        oneYearReturnPer: item.OneYearReturnPer,
        salesCompany: item.SalesCompany,
        standardPrice: item.StandardPrice,
        standardPriceDate: item.StandardPriceDate,
        symbol: item.Symbol,
        symbolName: item.SymbolName,
        trustFeePer: item.TrustFeePer,
      };
    }),
    assetType: res_.AssetType as PortfolioAssetType,
    benchMark: res_.BenchMark,
    currentPer: res_.CurrentPer,
    dissociatePer: res_.DissociatePer,
    targetPer: res_.TargetPer,
    targetPurchaseAmount: res_.TargetPurchaseAmount,
  };

  return res as InvestmentTrustFund;
};

export default PortfolioAnalysisInvestmentProduct;
